<template>
    <!-- THE SHEET, ON THE PAGE, BEFORE IT IS A FILE.
      --
      -- The first version had none, and the report was the obvious
      -- consequence: the only way to see what the settings did was to
      -- download the result, look at it, change something and download it
      -- again. Every other tool here shows its work on the stage; this one
      -- sent you to your downloads folder to find out.
      --
      -- THE PREVIEW IS THE FILE. Not a representation of it -- the same
      -- canvas drawSheet() produces, shown at whatever width the column has
      -- and handed over byte for byte when Save is pressed. That is the rule
      -- lib/meme.js exists to enforce, taken further than the meme page can:
      -- there is no second renderer here to disagree with, because there is
      -- only one canvas.
      --
      -- Which is also why the pictures are draggable. Rearranging is a thing
      -- you do by looking, so the answer to "move things around" is to let
      -- the picture be moved rather than to add a list of filenames with
      -- arrows beside them. -->
    <div id="sheet-preview" class="flex flex-col gap-3">
        <div class="flex flex-wrap items-baseline justify-between gap-2">
            <h3 class="text-lg font-bold">{{ t('sheet.previewTitle') }}</h3>
            <span v-if="size" data-sheet-size
                  class="text-[13px] text-ink-mute tabular-nums">{{ size }}</span>
        </div>

        <div
             class="relative rounded-2xl border border-rule bg-raised p-3 overflow-hidden">
            <!-- Hidden until it has been drawn once. A canvas that has never
              -- been sized is 300x150, and `w-full` stretches that into a
              -- squat empty box across the column -- a flash of something
              -- that looks broken, at the moment the reader first looks. -->
            <!-- touch-manipulation, NOT touch-none.
              --
              -- touch-none was here so a drag would reach the handlers, and
              -- on a phone it turned the whole preview into a place the page
              -- cannot be scrolled. Measured on a 412x915 screen: the canvas
              -- is 339x344, which is 38% of the screen -- and more than half
              -- of it once the sheet is tall -- sitting in the middle of a
              -- 5,143px document that a swipe there refuses to move.
              --
              -- So the page scrolls by default and the gesture ASKS for the
              -- touch instead: a press and hold arms it, and only then is
              -- scrolling blocked, by preventDefault on a non-passive
              -- touchmove. That works because arming requires a finger that
              -- has not moved, so the browser has not begun a scroll to take
              -- back. If any of it fails the failure is a page that scrolls,
              -- which is the right way round. -->
            <canvas ref="canvas" data-sheet-canvas v-show="plan"
                    class="block w-full h-auto rounded-lg touch-manipulation select-none"
                    :class="drag.from >= 0 ? 'cursor-grabbing' : 'cursor-grab'"
                    @pointerdown="onDown" @pointermove="onMove"
                    @pointerup="onUp" @pointercancel="onUp"></canvas>

            <!-- The cell being dragged, and the one it would land on. Drawn
              -- as elements over the canvas rather than painted into it: the
              -- canvas IS the output, and a highlight baked into it would be
              -- in the downloaded file. -->
            <div v-if="!plan" data-sheet-building
                 class="flex h-40 items-center justify-center text-[15px] text-ink-mute">
                {{ size || t('sheet.building') }}
            </div>

            <div v-if="markFrom" class="pointer-events-none absolute rounded-md ring-2 ring-white/80"
                 :style="markFrom"></div>
            <!-- A SOLID TOKEN, not bg-brand/20. An opacity modifier on a
              -- palette token generates no CSS at all -- token() is
              -- var(--name) with no <alpha-value> in it -- so the wash this
              -- was written as had never once rendered. tests/test_opacity.py
              -- is what found it; the tint paints the destination cell as a
              -- slot, which is what a drop target is. -->
            <div v-if="markTo" class="pointer-events-none absolute rounded-md ring-4 ring-brand bg-brand-tint"
                 :style="markTo"></div>
        </div>

        <!-- The gesture differs by layout, so the sentence does. Saying
          -- "drag to recentre" where nothing is cropped would describe
          -- something that cannot happen. -->
        <!-- A finger and a mouse are told different things, because they do
          -- different things: one presses and holds first. Asked of the
          -- pointer rather than of the user agent -- a laptop with a
          -- touchscreen answers "fine" and gets the mouse sentence, which is
          -- the one it can act on. -->
        <p class="text-[13px] text-ink-mute leading-snug">{{
            coarse
                ? (uiStore.sheetMode === 'fill' ? t('sheet.holdNoteFill') : t('sheet.holdNote'))
                : (uiStore.sheetMode === 'fill' ? t('sheet.dragNoteFill') : t('sheet.dragNote'))
        }}</p>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onBeforeUnmount, nextTick } from "vue"
import { useI18n } from "vue-i18n"
import { useUiElements } from "./../store/uiElements"
import { drawSheet, sheetPlan, cellAtPoint, moveItem, panStep, SHEET_GAPS }
    from "./../lib/contactSheet"

const { t } = useI18n()
const uiStore = useUiElements()

const canvas = ref(null)
const plan = ref(null)
// The pictures in the order they were drawn, so a cell index can be turned
// back into the row it came from -- which is what a focus is keyed by.
const shown = ref([])
const size = ref("")
const over = ref(-1)

/* THE DECODED PICTURES, CACHED BY THE ROW THEY CAME FROM.
 *
 * Every control change re-renders, and decoding twenty JPEGs per keystroke of
 * a slider is not a thing to do. Keyed by the batch item's blob URL, so a row
 * that finishes late or is re-run gets decoded once and no more.
 *
 * Decoded through an <img> rather than createImageBitmap for the reason
 * loadImage() in lib/imageTransform.js records: an <img> applies EXIF
 * orientation on its own and createImageBitmap does not agree across browsers
 * about whether it should, so a phone photograph would land on the sheet on
 * its side in some browsers and not others.
 */
const decoded = new Map()

async function pictures() {
    const rows = uiStore.batchItems.filter((i) => i.status === "done" && i.blob)
    const out = []
    for (const item of rows) {
        let img = decoded.get(item.blob)
        if (!img) {
            img = await new Promise((resolve) => {
                const el = new Image()
                el.onload = () => resolve(el)
                el.onerror = () => resolve(null)
                el.src = item.blob
            })
            if (img) {
                decoded.set(item.blob, img)
            }
        }
        if (img) {
            out.push({img, name: uiStore.batchOutputName(item),
                      // The row's own id, so a focus follows its picture
                      // through any amount of rearranging.
                      id: item.id,
                      focus: uiStore.sheetFocus[item.id] || null,
                      w: img.naturalWidth, h: img.naturalHeight})
        }
    }
    return out
}

/* The order the visitor put them in, falling back to the order they arrived.
 *
 * Indexes rather than ids, and clamped on every read: a saved order outlives
 * the set it was made for -- drop a different folder and the stored [3,0,1,2]
 * would otherwise address rows that are no longer there. Anything the current
 * set does not have is dropped, anything new is appended.
 */
function ordered(list) {
    const want = Array.isArray(uiStore.sheetOrder) ? uiStore.sheetOrder : []
    const kept = want.filter((i) => i >= 0 && i < list.length)
    const seen = new Set(kept)
    for (let i = 0; i < list.length; i++) {
        if (!seen.has(i)) {
            kept.push(i)
        }
    }
    return kept.map((i) => list[i])
}

/* WIDTH IS A CONSTANT, NOT THE COLUMN'S.
 *
 * It used to be the host column doubled, on the reasoning that a collage
 * justifies to the width it is given, so the sheet's proportions should follow
 * the space it is shown in. That is an argument about the PREVIEW, and this
 * canvas is the FILE -- the whole point of the component. So the window was
 * deciding what somebody downloaded, and the smaller the window the worse the
 * file.
 *
 * Measured on mixed-shape photographs, smallest cell on the sheet:
 *
 *                     20 pictures   30 pictures
 *     desktop, 1420px     233 px        188 px
 *     phone,    736px     116 px         94 px   <- unusable, at the CURRENT
 *                                                   limit of 20
 *
 * A phone produced a visibly worse collage than a laptop for the same
 * pictures, and nothing said so. At a fixed 2400 the same sets give 403 px and
 * 327 px, and 50 pictures still gives 221 px.
 *
 * 2400 RATHER THAN SOME OTHER NUMBER, because it is what the grid already
 * produces: SHEET_CELL is 400 and MAX_SHEET_COLUMNS is 6. So the two layouts
 * now export at one scale instead of two, and a sheet is the same file on
 * every device.
 *
 * The preview is unaffected in appearance: the canvas is `w-full h-auto`, so
 * CSS fits it to the column whatever its intrinsic size, and both toCanvas()
 * and box() map between the two by ratio. The PAN is unaffected too, and that
 * is arithmetic rather than luck: a pointer delta in canvas pixels scales with
 * the width, panStep() divides by draw.w which scales with it as well, so the
 * product -- the focus delta -- is invariant.
 *
 * WHAT IT COSTS, measured in a real browser on mixed shapes, median of nine
 * draws (software rendering, so a device with an accelerated canvas should do
 * better; not verified):
 *
 *     width 1420   n=20   15.2 ms      n=50   14.1 ms
 *     width 2400   n=20   19.1 ms      n=50   23.1 ms  (worst 35.8)
 *
 * A pan calls render() on every pointermove, so this is about one and a half
 * frames per move instead of one. The busy/again flag below keeps the work
 * from piling up -- it self-throttles to the draw rate rather than queueing --
 * so the effect is a pan that tracks at roughly 40 fps instead of 60, not a
 * backlog. Accepted: a file whose quality depended on the window size was the
 * worse fault by a long way.
 *
 * If it is ever reported as sluggish, the fix is to redraw only the cell being
 * panned rather than the whole sheet. That is a real change to the "the
 * preview IS the file" invariant, so it needs its own thought.
 */
const SHEET_WIDTH = 2400

function sheetWidth() {
    return SHEET_WIDTH
}

/* COALESCE, BUT NEVER DROP.
 *
 * The first version was `if (busy) return`, which is the obvious way to keep
 * a burst of control changes from starting five renders at once -- and it
 * silently LOSES the last one. A resize that lands while a render is in
 * flight returns immediately, and the sheet then stays at the old width with
 * nothing scheduled to fix it. The same class of fault as a watcher that
 * tracks nothing: everything looks fine and one input stopped working.
 *
 * So a request during a render sets `again` instead, and the render loops.
 */
let busy = false
let again = false

async function render() {
    if (busy) {
        again = true
        return
    }
    busy = true
    try {
        do {
            again = false
            await draw()
        } while (again)
    } finally {
        busy = false
    }
}

async function draw() {
    await nextTick()

    const list = ordered(await pictures())
    const target = canvas.value
    if (!target) {
        return
    }
    if (list.length < 2) {
        plan.value = null
        size.value = ""
        return
    }

    const opts = {
        images: list,
        mode: uiStore.sheetMode,
        columns: uiStore.sheetColumns,
        gap: SHEET_GAPS[uiStore.sheetGap] === undefined
            ? SHEET_GAPS.thin : SHEET_GAPS[uiStore.sheetGap],
        labels: uiStore.sheetLabels,
        ground: uiStore.sheetGround,
        width: sheetWidth()
    }
    const made = drawSheet(opts)
    if (!made) {
        plan.value = null
        size.value = t("sheet.tooBig")
        return
    }

    // The plan is kept for hit-testing. Recomputed from the same arguments
    // rather than returned by drawSheet(), which would make the painter's
    // signature about the pointer -- and asking sheetPlan() twice with one
    // set of arguments cannot give two answers.
    shown.value = list
    plan.value = sheetPlan({
        items: list.map((e) => ({w: e.w, h: e.h, focus: e.focus})),
        // No `ground` here on purpose: it decides what is PAINTED, not where
        // anything sits, so the hit-testing plan is identical without it.
        mode: opts.mode, columns: opts.columns, gap: opts.gap,
        labels: opts.labels, width: opts.width
    })

    target.width = made.width
    target.height = made.height
    target.getContext("2d").drawImage(made, 0, 0)
    size.value = t("sheet.pixels", {w: made.width, h: made.height})
}

/* Hand the canvas over as it stands. App.vue calls this, so Save cannot get a
 * different file from the one on screen -- there is nothing to re-render. */
function toBlob(type = "image/jpeg", quality = 0.9) {
    return new Promise((resolve) => {
        if (!canvas.value || !plan.value) {
            resolve(null)
            return
        }
        canvas.value.toBlob(resolve, type, quality)
    })
}

defineExpose({toBlob, render})

// ---- dragging one picture onto another --------------------------------

/* Where a pointer is, in the drawing's own pixels.
 *
 * The canvas is shown at the column's width and drawn at twice it, so
 * everything a pointer says has to be taken back into canvas pixels before it
 * can be compared with a plan.
 */
function toCanvas(ev) {
    const el = canvas.value
    if (!el) {
        return null
    }
    const r = el.getBoundingClientRect()
    if (!r.width || !r.height) {
        return null
    }
    return {x: ((ev.clientX - r.left) / r.width) * el.width,
            y: ((ev.clientY - r.top) / r.height) * el.height}
}

function at(ev) {
    const p = toCanvas(ev)
    return (p && plan.value) ? cellAtPoint(plan.value, p.x, p.y) : -1
}

function cellFor(index) {
    return plan.value ? plan.value.cells.find((c) => c.index === index) : null
}

function box(index) {
    const el = canvas.value
    const c = cellFor(index)
    if (!c || !el || !el.width) {
        return null
    }
    // Back into CSS pixels, plus the wrapper's own padding.
    const s = el.clientWidth / el.width
    return {
        left: `${12 + c.cell.x * s}px`,
        top: `${12 + c.cell.y * s}px`,
        width: `${c.cell.w * s}px`,
        height: `${c.cell.h * s}px`
    }
}

const markFrom = computed(() =>
    (drag.value.from >= 0 && !drag.value.panned) ? box(drag.value.from) : null)
const markTo = computed(() =>
    (drag.value.from >= 0 && !drag.value.panned
     && over.value >= 0 && over.value !== drag.value.from)
        ? box(over.value) : null)

/* ONE GESTURE, TWO JOBS, AND NO MODE TO SWITCH.
 *
 * Dragging a picture onto ANOTHER moves it there; dragging it around inside
 * its own cell slides the crop. Which one is happening is decided by where
 * the pointer goes, not by a toggle the reader has to find first -- and the
 * two cannot be confused, because leaving the cell is exactly the thing that
 * turns a recentre into a reorder.
 *
 * A picture that cannot slide never starts a pan: a square in a square cell
 * has nothing to move, and neither does anything in a collage, where nothing
 * is cropped. The plan says so per cell.
 */
const drag = ref({from: -1, panned: false})
let grip = null

function focusOf(index) {
    const e = shown.value[index]
    const f = e && uiStore.sheetFocus[e.id]
    return {x: (f && Number.isFinite(f.x)) ? f.x : 0.5,
            y: (f && Number.isFinite(f.y)) ? f.y : 0.5}
}

/* A FINGER HAS TO ASK. A mouse does not.
 *
 * A pointer that is a mouse or a pen has a cursor and no other use for a
 * drag, so it starts one immediately, exactly as before. A finger is also
 * how the page is scrolled, so it has to say which it means: hold still for
 * a moment and the drag arms; move first and the browser keeps the gesture
 * and scrolls, which is what it should do with a swipe across a picture.
 *
 * 320ms and 12px are the usual figures for this and are not measured here --
 * they want a real phone, which is the one thing this change has not had.
 */
const LONG_PRESS_MS = 320
const PRESS_SLOP_PX = 12
const armed = ref(false)
let waiting = null

/* Whether the thing pointing at this is a finger. Read once -- a device does
 * not grow a mouse mid-session, and a laptop with a touchscreen reports
 * "fine", which is right: it has a cursor and the mouse sentence is the one
 * it can act on. */
const coarse = typeof window !== "undefined" && window.matchMedia
    ? window.matchMedia("(pointer: coarse)").matches
    : false

function cancelWait() {
    if (waiting) {
        clearTimeout(waiting.timer)
        waiting = null
    }
}

function begin(i, p, pointerId) {
    const c = cellFor(i)
    const e = shown.value[i]
    const pan = (c && c.pan) ? c.pan : {x: 0, y: 0}
    grip = {
        start: p,
        focus: focusOf(i),
        pan,
        // Whether this picture had a focus of its own BEFORE the drag. A pan
        // that turns into a reorder has to be undone, and undoing it by
        // writing 0.5/0.5 would leave an entry behind -- enough to keep the
        // "put them back" button on screen for something nobody did.
        had: !!(e && uiStore.sheetFocus[e.id]),
        step: {
            x: c ? panStep(c.src.w, c.draw.w, pan.x) : 0,
            y: c ? panStep(c.src.h, c.draw.h, pan.y) : 0
        }
    }
    drag.value = {from: i, panned: false}
    over.value = i
    armed.value = true
    try {
        canvas.value.setPointerCapture(pointerId)
    } catch (err) { /* capture is a nicety; the handlers fire either way */ }
}

function onDown(ev) {
    const i = at(ev)
    const p = toCanvas(ev)
    if (i < 0 || !p) {
        return
    }
    if (ev.pointerType === "touch") {
        cancelWait()
        // Deliberately NOT preventDefault here: until the press is recognised
        // this touch still belongs to the page, and taking it would put the
        // dead zone straight back.
        waiting = {
            i, p, x: ev.clientX, y: ev.clientY, pointerId: ev.pointerId,
            timer: setTimeout(() => {
                const w = waiting
                waiting = null
                if (w) {
                    begin(w.i, w.p, w.pointerId)
                }
            }, LONG_PRESS_MS)
        }
        return
    }
    begin(i, p, ev.pointerId)
    ev.preventDefault()
}

// Put the crop back where the drag found it, and leave no entry behind if
// there was none to begin with.
function undoPan(id) {
    const next = Object.assign({}, uiStore.sheetFocus)
    if (grip && grip.had) {
        next[id] = grip.focus
    } else {
        delete next[id]
    }
    uiStore.sheetFocus = next
}

/* WHICH GESTURE THIS IS, DECIDED CONTINUOUSLY RATHER THAN ONCE.
 *
 * The first version decided on the first pointermove, and that made
 * reordering unreachable in cropped squares: a drag always begins inside the
 * cell it started in, so `panned` latched true on move one and never cleared,
 * and by the time the pointer arrived anywhere else the drop was being
 * refused. Reported as exactly that -- "the squares, cropped sheet has lost
 * the ability to reorder".
 *
 * So the question is asked on every move. Inside the starting square it is a
 * recentre; the moment the pointer leaves, whatever was recentred is put back
 * and it becomes a move. Coming back in resumes the recentre from where the
 * drag began, because the focus is computed from the start point rather than
 * accumulated.
 *
 * That only works because a pan can always be completed WITHOUT leaving the
 * square -- see panStep() in lib/contactSheet.js, which is the other half of
 * this fix.
 */
function onMove(ev) {
    if (waiting) {
        // Moved before the press was recognised: this is a scroll, and the
        // browser is already doing it.
        const dx = ev.clientX - waiting.x
        const dy = ev.clientY - waiting.y
        if (dx * dx + dy * dy > PRESS_SLOP_PX * PRESS_SLOP_PX) {
            cancelWait()
        }
        return
    }
    if (drag.value.from < 0 || !grip) {
        return
    }
    const here = at(ev)
    const p = toCanvas(ev)
    const e = shown.value[drag.value.from]
    if (!p || !e) {
        return
    }

    const canPan = grip.pan.x > 0 || grip.pan.y > 0
    if (canPan && here === drag.value.from) {
        /* DRAGGING RIGHT MOVES THE PICTURE RIGHT, which means the window
         * slides LEFT across it -- hence the minus. Getting this backwards is
         * the kind of thing that feels broken rather than wrong. */
        const clamp = (v) => Math.min(1, Math.max(0, v))
        uiStore.sheetFocus = Object.assign({}, uiStore.sheetFocus, {
            [e.id]: {
                x: grip.pan.x
                    ? clamp(grip.focus.x - (p.x - grip.start.x) * grip.step.x)
                    : 0.5,
                y: grip.pan.y
                    ? clamp(grip.focus.y - (p.y - grip.start.y) * grip.step.y)
                    : 0.5
            }
        })
        drag.value = {from: drag.value.from, panned: true}
        over.value = here
        render()
        return
    }

    if (drag.value.panned) {
        undoPan(e.id)
        drag.value = {from: drag.value.from, panned: false}
        render()
    }
    over.value = here
}

function onUp(ev) {
    cancelWait()
    armed.value = false
    const from = drag.value.from
    if (from < 0) {
        return
    }
    const to = at(ev)
    const panned = drag.value.panned
    drag.value = {from: -1, panned: false}
    over.value = -1
    grip = null
    // A pan is not a move. Once the crop has been slid, letting go over a
    // neighbouring cell must not also send the picture there.
    if (!panned && to >= 0 && to !== from) {
        const n = plan.value ? plan.value.cells.length : 0
        const current = ordered(Array.from({length: n}, (_, i) => i))
        // `from` and `to` are positions on the SHEET; the store holds
        // positions in the batch. Mapping through the current arrangement is
        // what keeps a second drag from addressing the first one's order.
        uiStore.sheetOrder = moveItem(current, from, to)
        render()
    }
}

// ---- when to redraw ----------------------------------------------------

/* Below what it reads, which is the rule CLAUDE.md records paying for: a
 * watch() getter runs immediately, Vue swallows the ReferenceError, and the
 * watcher then tracks nothing and never fires again. */
watch(() => [
    uiStore.sheetMode, uiStore.sheetGap, uiStore.sheetLabels,
    uiStore.sheetColumns, uiStore.sheetGround,
    uiStore.batchItems.filter((i) => i.status === "done").length
], render)

/* THE ONLY THING THAT ACTUALLY STOPS THE PAGE MOVING, and it has to be a
 * listener rather than a class: touch-action is read when the touch starts,
 * so changing it once a drag is under way does nothing to that drag.
 * preventDefault on a non-passive touchmove does, and it is safe here
 * because arming needs a finger that has not moved -- there is no scroll in
 * flight to cancel.
 *
 * Registered by hand rather than with @touchmove, because Vue's listener is
 * passive by default on this event and a passive handler may not
 * preventDefault at all.
 */
function blockScroll(ev) {
    if (armed.value) {
        ev.preventDefault()
    }
}

/* NO ResizeObserver ANY MORE. There was one, re-rendering whenever the column
 * changed width, because the sheet's own width used to be the column's. With
 * the width fixed it would redraw several megapixels to produce exactly the
 * file it already had -- on every drag of a window edge and on every phone
 * rotation. The canvas is `w-full h-auto`, so CSS alone refits the preview. */
onMounted(() => {
    render()
    if (canvas.value) {
        canvas.value.addEventListener("touchmove", blockScroll, {passive: false})
    }
})
onBeforeUnmount(() => {
    cancelWait()
    if (canvas.value) {
        canvas.value.removeEventListener("touchmove", blockScroll)
    }
    decoded.clear()
})
</script>
